<!--
  Code tab body — gated by `show_code` on the embed token.

  Renders the raw (pre-SQL-rewrite) insight source above three chrome
  affordances:
    - copy-to-clipboard button (clipboard API with `execCommand`
      fallback for non-secure contexts)
    - quick-look stats table derived from the source body via regex
    - regex-tokenised JS syntax highlighter — no Prism / hljs because
      the embed bundle is already ~17MB and a one-tab debug affordance
      doesn't justify the dep

  The parent (`EmbedHost.vue`) decides when to mount us — only when
  both `show_code` is true on the verified embed token AND the user
  has the Code tab active. The tab strip button itself lives in
  `EmbedHost` because it's part of the chrome row that gates Queries
  and Versions too; everything beneath that single button lives
  here so `EmbedHost` stays lean.

  Stats counts are raw regex hits (don't strip strings / comments
  out first), which is honest noise — the column labels say "calls"
  / "slots" so a reader doesn't misread them as definitive.
-->

<script setup lang="ts">
import { computed, ref } from "vue";

const props = defineProps<{
  /**
   * Raw insight source (pre-SQL-rewrite). Empty for missing payloads
   * — the template renders the "Source unavailable" empty state in
   * that case.
   */
  sourceCode: string;
  /** SBI API version the insight was authored against. Optional. */
  apiVersion?: string;
  /** Insight version number. Optional. */
  version?: number | null;
}>();

// ─── Clipboard ──────────────────────────────────────────────────────
/**
 * Transient flag flipped on by the copy button so the UI can briefly
 * swap the label to "Copied". Reset by a `setTimeout` the click
 * handler schedules — kept local to this component so a parent
 * remount of the panel resets it cleanly.
 */
const codeCopied = ref(false);

/**
 * Copy the raw insight source to the clipboard. `navigator.clipboard`
 * is the modern path; we fall back to a hidden `<textarea>` +
 * `document.execCommand("copy")` for older browsers / non-secure
 * contexts where the async API isn't available. The label flips to
 * "Copied" for ~1.5s on success; failures swallow silently — there's
 * no UX value in surfacing a "couldn't copy" error inside the embed.
 */
const copyCode = async (): Promise<void> => {
  const text = props.sourceCode;
  if (!text) return;
  let ok = false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      ok = true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  if (!ok) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      ok = document.execCommand("copy");
      document.body.removeChild(ta);
    } catch {
      ok = false;
    }
  }
  if (!ok) return;
  codeCopied.value = true;
  window.setTimeout(() => {
    codeCopied.value = false;
  }, 1500);
};

// ─── Syntax highlighter ─────────────────────────────────────────────
/**
 * Lightweight regex-based JS syntax highlighter for the Code tab.
 *
 * SBI source is plain JS (no JSX / TS), so a single tokenising pass
 * with one alternation regex is enough to colour keywords, strings,
 * comments, numbers, and the `sbi` namespace. Template-literal
 * interpolations (`${...}`) stay inside the string span — fine for
 * a read-only preview.
 *
 * Output is HTML; the caller renders it via `v-html` on the `<code>`
 * element. Every non-token slice is HTML-escaped before
 * concatenation so a stray `<` in the source can't produce real
 * markup.
 */
const HIGHLIGHT_KEYWORDS = new Set([
  "const", "let", "var", "function", "return", "if", "else", "for",
  "while", "do", "switch", "case", "default", "break", "continue",
  "new", "typeof", "instanceof", "in", "of", "throw", "try", "catch",
  "finally", "async", "await", "yield", "true", "false", "null",
  "undefined", "this", "class", "extends", "super", "import", "export",
  "from", "as", "void", "delete",
]);

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Order inside the alternation matters: comments and strings must
// come before identifiers / numbers so a `//` inside a string doesn't
// flip into comment mode and vice versa.
const HIGHLIGHT_RE =
  /\/\/[^\n]*|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b\d+(?:\.\d+)?\b|\b[A-Za-z_$][\w$]*\b/g;

const highlightSource = (src: string): string => {
  if (!src) return "";
  let out = "";
  let last = 0;
  for (const m of src.matchAll(HIGHLIGHT_RE)) {
    const i = m.index ?? 0;
    if (i > last) out += escapeHtml(src.slice(last, i));
    const tok = m[0];
    let cls: string | null = null;
    if (tok.startsWith("//") || tok.startsWith("/*")) cls = "tok-comment";
    else if (tok[0] === '"' || tok[0] === "'" || tok[0] === "`") cls = "tok-string";
    else if (tok[0] >= "0" && tok[0] <= "9") cls = "tok-number";
    else if (HIGHLIGHT_KEYWORDS.has(tok)) cls = "tok-keyword";
    else if (tok === "sbi") cls = "tok-sbi";
    out += cls
      ? `<span class="${cls}">${escapeHtml(tok)}</span>`
      : escapeHtml(tok);
    last = i + tok.length;
  }
  if (last < src.length) out += escapeHtml(src.slice(last));
  return out;
};

const highlightedCode = computed(() => highlightSource(props.sourceCode));

/**
 * Newline-joined line numbers — rendered as a left gutter beside the
 * code body. Precomputed here so the template doesn't have to loop;
 * the `<pre>` gutter and the `<pre>` body share the same line-height,
 * so a single column of right-aligned numbers lines up with the code
 * row-for-row.
 */
const lineNumbers = computed(() => {
  const n = props.sourceCode ? props.sourceCode.split("\n").length : 0;
  let out = "";
  for (let i = 1; i <= n; i++) out += i + "\n";
  // Drop the trailing newline so the gutter doesn't render an extra
  // blank row past the last code line.
  return out.slice(0, -1);
});

// ─── Stats ─────────────────────────────────────────────────────────
/**
 * Quick-look stats derived from the raw source. Surfaced in a small
 * table above the code panel so an author / debugger can eyeball the
 * shape of an insight without reading the whole body — e.g. "this
 * one fires 12 queries, is that a lot?".
 *
 * Counts are raw regex hits and so include matches inside string
 * literals and comments. Stripping those out would require a real
 * tokeniser; the false-positive rate is low enough on real insights
 * that the simpler approach reads as honest noise. Column labels
 * say "calls" / "slots" so a viewer doesn't misread a number as
 * authoritative.
 */
interface CodeStats {
  /** UTF-8 byte size of the source. */
  bytes: number;
  /** Total newline-separated lines (including blank). */
  lines: number;
  /** Lines that contain non-whitespace content. */
  nonBlankLines: number;
  /** Any `sbi.<thing>` reference — useful as a top-level health number. */
  sbiCalls: number;
  /** `sbi.sql\`…\`` + `sbi.data.query(` + `sbi.data.derive(` — every data marker. */
  queries: number;
  /** `sbi.state(...)` declarations. */
  states: number;
  /** `sbi.format(...)` registrations. */
  formats: number;
  /** `sbi.viz.kpi(...)` call sites. */
  kpis: number;
  /** `sbi.viz.table(...)` call sites. */
  tables: number;
  /** `sbi.viz.*` minus kpi/table — line, bar, sankey, etc. */
  charts: number;
  /** `sbi.ui.*` call sites — text/columns/tabs/callout/divider/section/accordion. */
  uiComponents: number;
  /** `sbi.branch.*` call sites — `if`, `when`. */
  branches: number;
  /** `sbi.filters.*` call sites — select, multiselect, toggle, date, etc. */
  filters: number;
  /** `sbi.drilldown(...)` call sites — state-gated detail regions. */
  drilldowns: number;
  /** `sbi.onClick(...)` registrations. */
  clickHandlers: number;
  /** TODO / FIXME / XXX markers (anywhere in source, not just comments). */
  todos: number;
}

const codeStats = computed<CodeStats | null>(() => {
  const src = props.sourceCode;
  if (!src) return null;
  const lines = src.split("\n");
  return {
    // `Blob` gives us the same UTF-8 byte length the network sent
    // without pulling a `TextEncoder` indirection. Same path the
    // dev-tools "size" column uses.
    bytes: new Blob([src]).size,
    lines: lines.length,
    nonBlankLines: lines.reduce(
      (n, l) => (l.trim().length > 0 ? n + 1 : n),
      0,
    ),
    sbiCalls: (src.match(/\bsbi\.[A-Za-z_$]/g) ?? []).length,
    // Every data primitive — `sbi.sql\`…\``, `sbi.data.query(...)`,
    // `sbi.data.derive(...)`. The latter two are JS-side; the tagged
    // template is the marker form. We count all three because each
    // adds a row to the per-pass query batch.
    queries: (
      src.match(/\bsbi\.(?:sql\s*`|data\.query\s*\(|data\.derive\s*\()/g) ?? []
    ).length,
    states: (src.match(/\bsbi\.state\s*\(/g) ?? []).length,
    formats: (src.match(/\bsbi\.format\s*\(/g) ?? []).length,
    kpis: (src.match(/\bsbi\.viz\.kpi\s*\(/g) ?? []).length,
    tables: (src.match(/\bsbi\.viz\.table\s*\(/g) ?? []).length,
    // `viz.*` minus kpi/table. Counted by subtraction so we don't have
    // to enumerate every chart method — the regex above already pins
    // kpi and table separately, so total-viz minus those two is the
    // chart count.
    charts: Math.max(
      0,
      (src.match(/\bsbi\.viz\.[A-Za-z_$]/g) ?? []).length
        - (src.match(/\bsbi\.viz\.kpi\s*\(/g) ?? []).length
        - (src.match(/\bsbi\.viz\.table\s*\(/g) ?? []).length,
    ),
    uiComponents: (src.match(/\bsbi\.ui\.[A-Za-z_$]/g) ?? []).length,
    branches: (src.match(/\bsbi\.branch\.[A-Za-z_$]/g) ?? []).length,
    filters: (src.match(/\bsbi\.filters\.[A-Za-z_$]/g) ?? []).length,
    drilldowns: (src.match(/\bsbi\.drilldown\s*\(/g) ?? []).length,
    clickHandlers: (src.match(/\bsbi\.onClick\s*\(/g) ?? []).length,
    todos: (src.match(/\b(?:TODO|FIXME|XXX)\b/g) ?? []).length,
  };
});

/** Human-readable byte size. Uses 1024-step KB so it matches dev-tools. */
const formatBytes = (n: number): string =>
  n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;

/** Group-separator integers so 12345 reads as "12,345". */
const formatInt = (n: number): string => n.toLocaleString();

// ─── Callouts: SQL queries + filters ────────────────────────────────
/**
 * One SQL callout — the body between the backticks of a `sbi.sql\`…\``
 * tagged template. Template-literal interpolations (`${expr}`) stay
 * inline; we don't try to substitute their JS values because this is
 * a static view of the source, not a runtime trace.
 */
interface SqlCallout {
  index: number;
  body: string;
}

/**
 * One filter callout — `sbi.filters.<type>(...)`. We capture the
 * function name (`select`, `multiselect`, `toggle`, etc.), pull the
 * `label: "…"` out of the opts object when present, and keep the
 * full call expression for the callout body.
 */
interface FilterCallout {
  index: number;
  type: string;
  label: string | null;
  snippet: string;
}

const sqlCallouts = computed<SqlCallout[]>(() => {
  const src = props.sourceCode;
  if (!src) return [];
  const out: SqlCallout[] = [];
  // `[^`\\]` excludes backticks and backslashes; `\\.` consumes
  // escaped chars. Inline `${expr}` runs through fine — none of `$`,
  // `{`, `}` are excluded. The `s` flag lets `.` (and the negated
  // class via lookbehind effects) span newlines.
  const re = /\bsbi\.sql\s*`((?:\\.|[^`\\])*)`/gs;
  let m: RegExpExecArray | null;
  let idx = 1;
  while ((m = re.exec(src)) !== null) {
    out.push({ index: idx++, body: m[1] ?? "" });
  }
  return out;
});

const filterCallouts = computed<FilterCallout[]>(() => {
  const src = props.sourceCode;
  if (!src) return [];
  const out: FilterCallout[] = [];
  const re = /\bsbi\.filters\.([A-Za-z_$][\w$]*)\s*\(/g;
  let m: RegExpExecArray | null;
  let idx = 1;
  while ((m = re.exec(src)) !== null) {
    const type = m[1] ?? "";
    const callStart = m.index;
    // Walk forward from just past the opening paren, tracking
    // parenthesis depth and skipping over string literals so a `)`
    // inside a string doesn't terminate the call expression early.
    let k = re.lastIndex;
    let depth = 1;
    while (k < src.length && depth > 0) {
      const c = src[k];
      if (c === "(") depth++;
      else if (c === ")") {
        depth--;
        if (depth === 0) {
          k++;
          break;
        }
      } else if (c === '"' || c === "'" || c === "`") {
        // Skip the string literal in one go — handles escapes.
        const quote = c;
        k++;
        while (k < src.length && src[k] !== quote) {
          if (src[k] === "\\") k++;
          k++;
        }
      }
      k++;
    }
    const snippet = src.slice(callStart, k);
    const labelMatch = snippet.match(/\blabel\s*:\s*["']([^"']+)["']/);
    out.push({
      index: idx++,
      type,
      label: labelMatch ? (labelMatch[1] ?? null) : null,
      snippet,
    });
  }
  return out;
});
</script>

<template>
  <div class="chrome-tab-body code-panel" role="tabpanel">
    <p v-if="!sourceCode" class="code-empty">
      Source unavailable.
    </p>
    <template v-else>
      <!--
        Stats grid. One tile per stat — a small uppercase label plus
        a prominent monospace number, with an optional dim sub-line
        for context (chars / non-blank lines / saved version). Two
        special tiles bend the rule: API version (string value, not a
        count) and TODOs (warning chip when non-zero).
      -->
      <div v-if="codeStats" class="stat-grid">
        <div class="stat-tile">
          <div class="stat-tile-label">Size</div>
          <div class="stat-tile-value">{{ formatBytes(codeStats.bytes) }}</div>
          <div class="stat-tile-sub">{{ formatInt(codeStats.bytes) }} chars</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Lines</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.lines) }}</div>
          <div class="stat-tile-sub">
            {{ formatInt(codeStats.nonBlankLines) }} non-blank
          </div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">API version</div>
          <div class="stat-tile-value stat-tile-value--text">
            {{ apiVersion || "—" }}
          </div>
          <div v-if="version !== null && version !== undefined" class="stat-tile-sub">
            v{{ version }}
          </div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">sbi.* calls</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.sbiCalls) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Queries</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.queries) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">State slots</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.states) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Formats</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.formats) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">KPIs</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.kpis) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Tables</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.tables) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Charts</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.charts) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">UI components</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.uiComponents) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Branches</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.branches) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Filters</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.filters) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Drilldowns</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.drilldowns) }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-tile-label">Click handlers</div>
          <div class="stat-tile-value">{{ formatInt(codeStats.clickHandlers) }}</div>
        </div>
        <div
          class="stat-tile"
          :class="{ 'stat-tile--warn': codeStats.todos > 0 }"
        >
          <div class="stat-tile-label">TODOs</div>
          <div class="stat-tile-value">
            {{ formatInt(codeStats.todos) }}
            <span v-if="codeStats.todos > 0" class="stat-tile-chip">⚠</span>
          </div>
        </div>
      </div>

      <!--
        Callout sections — surface the inlined SQL bodies and filter
        controls without making the reader hunt through the source.
        Each section only renders when its list is non-empty; the
        sections themselves are `<details>` so a query-heavy insight
        doesn't push the code card off the fold. Closed by default —
        the count chip in the summary row is the at-a-glance signal;
        a viewer opens the section when they want the bodies.
      -->
      <details v-if="sqlCallouts.length" class="callout-section">
        <summary class="callout-section-head">
          <h3 class="callout-section-title">SQL queries</h3>
          <span class="callout-section-count">{{ sqlCallouts.length }}</span>
        </summary>
        <ol class="callout-list">
          <li
            v-for="c in sqlCallouts"
            :key="c.index"
            class="callout-item"
          >
            <div class="callout-item-meta">
              <span class="callout-item-idx">#{{ c.index }}</span>
              <span class="callout-item-tag">sbi.query</span>
            </div>
            <pre class="callout-item-body"><code>{{ c.body.trim() }}</code></pre>
          </li>
        </ol>
      </details>

      <details v-if="filterCallouts.length" class="callout-section">
        <summary class="callout-section-head">
          <h3 class="callout-section-title">Filters</h3>
          <span class="callout-section-count">{{ filterCallouts.length }}</span>
        </summary>
        <ol class="callout-list">
          <li
            v-for="c in filterCallouts"
            :key="c.index"
            class="callout-item"
          >
            <div class="callout-item-meta">
              <span class="callout-item-idx">#{{ c.index }}</span>
              <span class="callout-item-tag">sbi.filters.{{ c.type }}</span>
              <span v-if="c.label" class="callout-item-label">
                “{{ c.label }}”
              </span>
            </div>
            <pre class="callout-item-body"><code>{{ c.snippet }}</code></pre>
          </li>
        </ol>
      </details>

      <!--
        Code card. Header carries the filename label, a terse "N lines
        · X.X KB" summary, and the Copy button so the toolbar isn't a
        separate row. Body is a two-column flex layout: line-number
        gutter on the left, highlighted source on the right; both
        share the same `line-height` so numbers align row-for-row.
        Scrolling lives on the body — the header stays put.
      -->
      <div class="code-card">
        <div class="code-card-head">
          <span class="code-card-label">insight.js</span>
          <span v-if="codeStats" class="code-card-meta">
            {{ formatInt(codeStats.lines) }} lines ·
            {{ formatBytes(codeStats.bytes) }}
          </span>
          <button
            type="button"
            class="code-copy-button"
            :disabled="!sourceCode"
            @click="copyCode"
          >
            {{ codeCopied ? "Copied" : "Copy" }}
          </button>
        </div>
        <div class="code-card-body">
          <pre class="code-gutter" aria-hidden="true">{{ lineNumbers }}</pre>
          <pre class="code-body"><code v-html="highlightedCode"></code></pre>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.code-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 8px 0 0;
}
.code-empty {
  margin: 0;
  padding: 16px 4px;
  color: var(--card-muted, #6b7280);
  font-size: 13px;
}

/* ---------- Stats grid ----------
 *
 * Auto-fill grid of small stat tiles. `minmax(140px, 1fr)` keeps the
 * tile width responsive — five per row on a typical insight-page
 * width, collapsing to two on narrow embeds without any media-query
 * fiddling. Tiles are intentionally low-chrome (1px rule, no
 * shadow) so the grid reads as a data summary, not a CTA. */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}
.stat-tile {
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  padding: 10px 12px;
  background: var(--card, #fff);
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.stat-tile--warn {
  border-color: var(--warn, #b08800);
  background: rgba(176, 136, 0, 0.04);
}
.stat-tile-label {
  font-family: var(--font, system-ui, sans-serif);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--card-muted, #6b7280);
}
.stat-tile-value {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 18px;
  font-weight: 600;
  color: var(--card-ink, #1a1a1a);
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* API-version tile holds a string instead of a number — drop the
 * monospace and tabular-nums so the label reads naturally. */
.stat-tile-value--text {
  font-family: var(--font, system-ui, sans-serif);
  font-size: 15px;
  font-weight: 600;
}
.stat-tile-sub {
  font-family: var(--font, system-ui, sans-serif);
  font-size: 11px;
  color: var(--card-muted, #6b7280);
  line-height: 1.2;
}
.stat-tile-chip {
  color: var(--warn, #b08800);
  font-size: 13px;
  font-weight: 500;
}

/* ---------- Callout sections ----------
 *
 * `<details>` blocks for SQL queries + filters. The `<summary>`
 * carries the section title + count chip and inherits the default
 * triangle marker; we restyle the marker subtly so it reads as part
 * of the affordance rather than a hint of browser chrome. Each list
 * item is a bordered card with a tiny meta row and a code body. */
.callout-section {
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  background: var(--card, #fff);
  overflow: hidden;
}
.callout-section[open] .callout-section-head {
  border-bottom: 1px solid var(--rule, #e5e7eb);
}
.callout-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  list-style: none;
  background: var(--rule-soft, rgba(0, 0, 0, 0.025));
  user-select: none;
}
.callout-section-head::-webkit-details-marker {
  display: none;
}
.callout-section-head::before {
  content: "▸";
  font-size: 10px;
  color: var(--card-muted, #6b7280);
  transition: transform 120ms;
  display: inline-block;
}
.callout-section[open] .callout-section-head::before {
  transform: rotate(90deg);
}
.callout-section-title {
  margin: 0;
  font-family: var(--font, system-ui, sans-serif);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--card-ink, #1a1a1a);
}
.callout-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: var(--card, #fff);
  border: 1px solid var(--rule, #e5e7eb);
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 10.5px;
  font-weight: 600;
  color: var(--card-muted, #6b7280);
  font-variant-numeric: tabular-nums;
}
.callout-list {
  list-style: none;
  margin: 0;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
}
.callout-item {
  border: 1px solid var(--rule, #e5e7eb);
  border-left: 3px solid var(--primary, #0777b3);
  border-radius: 4px;
  background: var(--card, #fff);
  padding: 8px 10px 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.callout-item-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  font-size: 11px;
}
.callout-item-idx {
  font-family: var(--mono, ui-monospace, monospace);
  font-weight: 600;
  color: var(--card-muted, #6b7280);
  font-variant-numeric: tabular-nums;
}
.callout-item-tag {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 10.5px;
  font-weight: 600;
  color: var(--primary-deep, #044f76);
  background: rgba(7, 119, 179, 0.08);
  padding: 1px 6px;
  border-radius: 3px;
}
.callout-item-label {
  font-family: var(--font, system-ui, sans-serif);
  font-size: 11.5px;
  font-style: italic;
  color: var(--card-ink, #1a1a1a);
}
.callout-item-body {
  margin: 0;
  padding: 6px 8px;
  background: var(--rule-soft, rgba(0, 0, 0, 0.035));
  border-radius: 3px;
  overflow-x: auto;
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 11.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--card-ink, #1a1a1a);
}

/* ---------- Code card ----------
 *
 * Bordered card with a header bar (filename / meta / copy) and a
 * two-column body (line-number gutter on the left, highlighted
 * source on the right). The body is the only scrolling region —
 * the header stays in place. Constrained to ~600px tall so the
 * stats grid above stays visible without scrolling the page. */
.code-card {
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  background: var(--card, #fff);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.code-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--rule, #e5e7eb);
  background: var(--rule-soft, rgba(0, 0, 0, 0.025));
}
.code-card-label {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--card-ink, #1a1a1a);
  letter-spacing: 0.01em;
}
.code-card-meta {
  flex: 1;
  font-family: var(--font, system-ui, sans-serif);
  font-size: 11px;
  color: var(--card-muted, #6b7280);
  font-variant-numeric: tabular-nums;
}
.code-copy-button {
  appearance: none;
  background: var(--card, #fff);
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: 4px;
  padding: 4px 10px;
  font-family: var(--font, system-ui, sans-serif);
  font-size: 11.5px;
  font-weight: 500;
  color: var(--card-ink, #1a1a1a);
  cursor: pointer;
  transition:
    background 120ms,
    border-color 120ms,
    color 120ms;
}
.code-copy-button:hover:not(:disabled) {
  border-color: var(--primary, #0777b3);
  color: var(--primary-deep, #044f76);
}
.code-copy-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.code-card-body {
  display: flex;
  overflow: auto;
  max-height: 600px;
  background: var(--card, #fff);
}
.code-gutter {
  margin: 0;
  padding: 12px 10px 12px 14px;
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 12px;
  line-height: 1.55;
  color: var(--card-muted, #6b7280);
  text-align: right;
  border-right: 1px solid var(--rule, #e5e7eb);
  background: var(--rule-soft, rgba(0, 0, 0, 0.02));
  white-space: pre;
  user-select: none;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
}
.code-body {
  margin: 0;
  padding: 12px 14px;
  flex: 1;
  min-width: 0;
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 12px;
  line-height: 1.55;
  white-space: pre;
  color: var(--card-ink, #1a1a1a);
  background: transparent;
}

/* Token colours for the regex-based JS highlighter.
 *
 * Two palettes ship: a light-mode default and a `prefers-color-scheme:
 * dark` override. We don't try to read theme variables for these
 * because syntax tokens need contrast against the *code background*,
 * which is the card's `--card` fill — most host themes don't define
 * a separate code-block palette, so embed-local hex values give a
 * predictable result whichever brand vars the host has set.
 *
 * Strings use the warm-red/coral convention (VS Code Light / Dark)
 * — distinct from numbers (brown / orange), keywords (purple), and
 * the `sbi` highlight (blue) in both modes.
 *
 * `:deep()` pierces `<style scoped>`: the spans are injected via
 * `v-html` on the `<code>` element and don't carry the component's
 * data-v attribute, so a plain `.tok-keyword` selector would miss
 * them. The outer `.code-body` keeps the rules scoped to the Code
 * tab so they can't bleed into other monospaced content. */
.code-body :deep(.tok-keyword) {
  color: #8250df;
  font-weight: 600;
}
.code-body :deep(.tok-string) {
  color: #a31515;
}
.code-body :deep(.tok-comment) {
  color: #6e7781;
  font-style: italic;
}
.code-body :deep(.tok-number) {
  color: #953800;
}
.code-body :deep(.tok-sbi) {
  color: var(--primary-deep, #0550ae);
  font-weight: 700;
}

@media (prefers-color-scheme: dark) {
  .code-body :deep(.tok-keyword) {
    color: #d2a8ff;
  }
  .code-body :deep(.tok-string) {
    color: #ff9b8a;
  }
  .code-body :deep(.tok-comment) {
    color: #8b949e;
  }
  .code-body :deep(.tok-number) {
    color: #ffa657;
  }
  .code-body :deep(.tok-sbi) {
    color: #79c0ff;
  }
}
</style>
